import { useState } from 'react';
import {
  Button,
  Flex,
  Icon,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalFooter,
  OptionCard,
  OptionCardDescription,
  OptionCardGroup,
  OptionCardTitle,
  Paragraph,
  Stack,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import type { CreditCardFormAddress } from '../utils/credit-card-form-submit';

type AddressChoice = 'original' | 'corrected';

export type UpdateAddressModalData = {
  inputAddress: CreditCardFormAddress;
  correctedAddress: CreditCardFormAddress;
};

/**
 * Managed modal shown when the address-validation service suggests a corrected billing address. The
 * user picks the address they entered or the suggested one; confirming resolves with the chosen
 * address (continuing the card submission), dismissing resolves as a cancel (aborting it).
 */
export function UpdateAddressModal({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<UpdateAddressModalData, CreditCardFormAddress>) {
  const { inputAddress, correctedAddress } = data;
  const [addressToUse, setAddressToUse] = useState<AddressChoice>('corrected');

  const handleConfirm = () => {
    onConfirm(addressToUse === 'corrected' ? correctedAddress : inputAddress);
  };

  return (
    <Modal state={modalState} title="Confirm your address">
      <ModalBody mb="300">
        <Stack gap="200">
          <Paragraph>The address you provided wasn't recognized, but we found a potential matching address.</Paragraph>
          <Paragraph weight="bold">Review the changes below and select an address to use.</Paragraph>
          <OptionCardGroup
            ariaLabel="Address to use"
            w="full"
            value={addressToUse}
            onChange={(value: string) => setAddressToUse(value === 'original' ? 'original' : 'corrected')}
          >
            <OptionCard value="original" flex={1} ariaLabel="Current address">
              <OptionCardTitle>Current address</OptionCardTitle>
              <OptionCardDescription>
                <AddressLines address={inputAddress} />
              </OptionCardDescription>
            </OptionCard>

            <Flex alignItems="center">
              <Icon size="xl" name="arrow-right" />
            </Flex>

            <OptionCard value="corrected" flex={1} ariaLabel="Suggested address">
              <OptionCardTitle>Suggested address</OptionCardTitle>
              <OptionCardDescription>
                <AddressLines address={correctedAddress} />
              </OptionCardDescription>
            </OptionCard>
          </OptionCardGroup>
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={handleConfirm}>
          Use this address
        </Button>
      </ModalFooter>
    </Modal>
  );
}

type AddressLinesProps = {
  address: CreditCardFormAddress;
};

function AddressLines({ address }: AddressLinesProps) {
  const cityLine = isEmptyString(address.state)
    ? `${address.city}, ${address.zipCode}`
    : `${address.city}, ${address.state}, ${address.zipCode}`;

  return (
    <List styleType="none">
      <ListItem>{address.addressLine1}</ListItem>
      {!isEmptyString(address.addressLine2) ? <ListItem>{address.addressLine2}</ListItem> : null}
      <ListItem>{cityLine}</ListItem>
      <ListItem>{address.country}</ListItem>
    </List>
  );
}
